import React, { createContext, useContext, useState, useEffect } from "react";

type Language = "en" | "sv";

interface Translations {
  [key: string]: string | Translations;
}

const translations: Record<Language, Translations> = {
  en: {
    app: {
      name: "aimtrain",
      tagline: "Professional Shooting Range Training"
    },
    home: {
      title: "Welcome to aimtrain",
      createTarget: "Create Target Sheet",
      createTargetDesc: "Design custom target sheets with different shapes and distances",
      startTraining: "Start Training",
      startTrainingDesc: "Timed training sessions with audio cues",
      shotDetection: "Shot Detection",
      shotDetectionDesc: "AI listens and detects shots via microphone",
      analyzeResults: "Analyze Results",
      analyzeResultsDesc: "AI-powered shot analysis and scoring",
      savedSheet: "Saved Target Sheet",
      targets: "targets",
      distance: "Distance",
      types: "Types"
    },
    creator: {
      title: "Create Target Sheets",
      distance: "Distance",
      targetCount: "Number of targets",
      targetTypes: "Target types",
      circle: "Circle",
      square: "Square",
      invertedTriangle: "Inverted Triangle",
      triangle: "Triangle",
      hexagon: "Hexagon",
      diamond: "Diamond",
      print: "Print / Save PDF",
      errorNotFound: "Could not find target sheet",
      errorPrintWindow: "Could not open print window",
      successPrint: "Press 'Save as PDF' or choose your printer",
      printTitle: "Target Sheet",
      maxTypes: "Maximum types reached. You can select up to"
    },
    targetSheet: {
      shotsPerTarget: "Shots/target",
      maxPoints: "Max pts"
    },
    training: {
      title: "Training Session",
      shotCount: "Number of Shots",
      interval: "Interval per shot (seconds)",
      sound: "Sound",
      shot: "Shot",
      start: "Start",
      continue: "Continue",
      pause: "Pause",
      complete: "Session complete - Analyze results",
      started: "Training started!",
      paused: "Training paused",
      reset: "Training reset",
      sessionComplete: "Training session complete!",
      shotNumber: "Shot #",
      shootTarget: "Shoot at the target!",
      paused2: "Paused",
      pressStart: "Press start"
    },
    analysis: {
      title: "Analyze Results",
      takePhoto: "Take photo with camera",
      or: "Or",
      uploadImage: "Upload image",
      analyzing: "Analyzing shots...",
      takeNewPhoto: "Take new photo",
      results: "Results",
      totalPoints: "Total Points",
      accuracy: "Accuracy",
      hits: "Hits",
      misses: "Misses",
      detailedBreakdown: "Detailed breakdown",
      target: "Target",
      shot: "Shot",
      ring: "Ring",
      holes: "holes",
      hole: "hole",
      noHole: "No hole",
      trainAgain: "New Analysis",
      complete: "Analysis complete!",
      error: "Could not analyze image",
      language: "en",
      invalidFile: "Invalid file type. Please select an image.",
      fileReadError: "Could not read file",
      invalidImageData: "Invalid image data"
    },
    common: {
      back: "Back",
      saving: "Saving..."
    },
    auth: {
      login: "Log In",
      signup: "Sign Up",
      loginDesc: "Log in to save your analyses",
      signupDesc: "Create an account to save your analyses",
      email: "Email",
      password: "Password",
      firstName: "First Name",
      lastName: "Last Name",
      country: "Country",
      loading: "Loading...",
      loginError: "Login failed. Please check your credentials.",
      signupError: "Sign up failed. Please try again.",
      loginSuccess: "Logged in successfully!",
      signupSuccess: "Account created successfully!",
      genericError: "An error occurred. Please try again.",
      switchToSignup: "Don't have an account? Sign up",
      switchToLogin: "Already have an account? Log in",
      forgotPassword: "Forgot Password",
      forgotPasswordLink: "Forgot your password?",
      forgotPasswordDesc: "Enter your email to reset your password",
      sendResetLink: "Send Reset Link",
      resetPassword: "Reset Password",
      resetPasswordError: "Failed to send reset email. Please try again.",
      resetPasswordSuccess: "Password reset email sent! Check your inbox.",
      backToLogin: "Back to login",
      logout: "Log Out"
    },
    history: {
      title: "My Analyses",
      loading: "Loading...",
      noAnalyses: "No saved analyses yet",
      deleteSuccess: "Analysis deleted",
      save: "Save Analysis",
      saveSuccess: "Analysis saved!"
    },
    shotDetection: {
      title: "Shot Detection Training",
      targetCount: "Number of Shots",
      sensitivity: "Detection Sensitivity",
      thresholdValue: "Threshold",
      shotsDetected: "Shots Detected",
      start: "Start Listening",
      stop: "Stop & Reset",
      audioLevel: "Audio Level",
      quiet: "Quiet",
      loud: "Loud",
      statistics: "Statistics",
      average: "Average",
      fastest: "Fastest",
      slowest: "Slowest",
      shotLog: "Shot Log",
      shot: "Shot",
      complete: "All shots detected!",
      started: "Listening for shots...",
      micError: "Could not access microphone",
      saveSession: "Save Session",
      saved: "Session saved successfully!",
      saveError: "Could not save session",
      noShots: "No shots to save",
      loginRequired: "You must be logged in to save sessions"
    },
    faq: {
      title: "Frequently Asked Questions",
      q1: "What is aimtrain?",
      a1: "aimtrain is a professional shooting training app that helps you improve your shooting skills through timed training sessions, shot detection, and AI-powered analysis.",
      q2: "How does shot detection work?",
      a2: "Shot detection uses your device's microphone to listen for and register shots. It measures the time between each shot and provides detailed statistics about your shooting performance.",
      q3: "Do I need to create targets before training?",
      a3: "No, you can start training immediately! The timed training mode allows you to set the number of shots and interval between them without creating target sheets.",
      q4: "How does the AI analysis work?",
      a4: "Upload or take a photo of your target after shooting. The AI will automatically detect and analyze each shot, calculating your total score, accuracy, and providing detailed feedback.",
      q5: "Can I save my training results?",
      a5: "Yes! Create an account to save your analyses and track your progress over time in the History section.",
      q6: "What target shapes are available?",
      a6: "You can create custom target sheets with various shapes including circles, squares, triangles, inverted triangles, hexagons, and diamonds at different distances."
    },
    about: {
      title: "About aimtrain",
      metaDescription: "Advanced air rifle training app with customizable target sheets, timed sequences, shot detection, and AI-powered analysis for professional shooting training",
      heading: "Professional Air Rifle Training Made Simple",
      intro: "aimtrain is your comprehensive digital companion for air rifle shooting training, combining traditional shooting methods with modern AI technology to help you improve your accuracy and consistency.",
      whatIsTitle: "What is aimtrain?",
      whatIsContent: "aimtrain is an advanced shooting training application designed for air rifle enthusiasts and professionals. Whether you're training for competitions or improving your recreational shooting skills, aimtrain provides the tools you need to track, analyze, and improve your performance.",
      featuresTitle: "Key Features",
      feature1Title: "Custom Target Creation",
      feature1Content: "Design and print customizable target sheets with various shapes (circles, squares, triangles, hexagons, diamonds) at different distances. Perfect for 25m, 50m, and other standard shooting distances.",
      feature2Title: "Timed Training Sessions",
      feature2Content: "Practice with realistic timed sequences featuring audio cues. Configure the number of shots and intervals to simulate competition conditions or build muscle memory.",
      feature3Title: "AI-Powered Shot Detection",
      feature3Content: "Use your device's microphone to automatically detect and log each shot. Get detailed statistics including timing, intervals, and shooting rhythm analysis.",
      feature4Title: "Intelligent Result Analysis",
      feature4Content: "Upload photos of your targets and let our AI analyze shot placement, calculate scores, and provide detailed feedback. Supports multiple target configurations including 1, 4, 6, and 9-target sheets.",
      whoForTitle: "Who is aimtrain for?",
      whoForContent: "aimtrain is designed for air rifle shooters of all levels - from beginners learning the fundamentals to competitive shooters preparing for championships. It's particularly useful for 10m air rifle disciplines and precision shooting training.",
      techTitle: "Technology",
      techContent: "Built with modern web technologies, aimtrain works on any device with a camera and microphone. The AI analysis uses advanced computer vision to accurately detect shot placement and calculate scores based on standard shooting target scoring systems.",
      privacyTitle: "Privacy & Data",
      privacyContent: "Your training data and analysis results are stored securely. Images are processed for analysis and can be saved to your account to track progress over time. We never share your personal training data with third parties."
    },
    feedback: {
      heading: "Help Improve AI Accuracy",
      instruction: "Your feedback helps train the AI to become more accurate. Please rate the analysis below:",
      question: "Was the AI analysis accurate?",
      thumbsUp: "Yes, accurate",
      thumbsDown: "No, needs correction",
      thankYou: "Thank you for your feedback!",
      correctScores: "Correct the scores below:",
      aiScore: "AI Score",
      range: "Range",
      submit: "Submit Corrections",
      correctionsSaved: "Corrections saved! This helps improve our AI.",
      loginRequired: "You must be logged in to provide feedback"
    }
  },
  sv: {
    app: {
      name: "aimtrain",
      tagline: "Professionell Skytteträning"
    },
    home: {
      title: "Välkommen till aimtrain",
      createTarget: "Skapa Måltavlor",
      createTargetDesc: "Designa anpassade måltavlor med olika former och avstånd",
      startTraining: "Starta Träning",
      startTrainingDesc: "Tidsbaserade träningssekvenser med ljudsignaler",
      shotDetection: "Skottdetektering",
      shotDetectionDesc: "AI lyssnar och detekterar skott via mikrofon",
      analyzeResults: "Analysera Resultat",
      analyzeResultsDesc: "AI-baserad analys av skott och poängräkning",
      savedSheet: "Sparad Måltavla",
      targets: "mål",
      distance: "Avstånd",
      types: "Typer"
    },
    creator: {
      title: "Skapa Måltavlor",
      distance: "Avstånd",
      targetCount: "Antal mål",
      targetTypes: "Måltyper",
      circle: "Cirkel",
      square: "Kvadrat",
      invertedTriangle: "Uppochnedvänd Triangel",
      triangle: "Triangel",
      hexagon: "Hexagon",
      diamond: "Diamant",
      print: "Skriv ut / Spara PDF",
      errorNotFound: "Kunde inte hitta måltavlan",
      errorPrintWindow: "Kunde inte öppna fönster för utskrift",
      successPrint: "Tryck 'Spara som PDF' eller välj din skrivare",
      printTitle: "Måltavla",
      maxTypes: "Max antal typer nått. Du kan välja upp till"
    },
    targetSheet: {
      shotsPerTarget: "Skott/mål",
      maxPoints: "Max p"
    },
    training: {
      title: "Träningssekvens",
      shotCount: "Antal Skott",
      interval: "Intervall per skott (sekunder)",
      sound: "Ljud",
      shot: "Skott",
      start: "Starta",
      continue: "Fortsätt",
      pause: "Pausa",
      complete: "Sekvens klar - Analysera resultat",
      started: "Träning startad!",
      paused: "Träning pausad",
      reset: "Träning återställd",
      sessionComplete: "Träningssekvens klar!",
      shotNumber: "Skott #",
      shootTarget: "Skjut på målet!",
      paused2: "Pausad",
      pressStart: "Tryck start"
    },
    analysis: {
      title: "Analysera Resultat",
      takePhoto: "Ta foto med kamera",
      or: "Eller",
      uploadImage: "Ladda upp bild",
      analyzing: "Analyserar skott...",
      takeNewPhoto: "Ta nytt foto",
      results: "Resultat",
      totalPoints: "Totala poäng",
      accuracy: "Träffsäkerhet",
      hits: "Träffar",
      misses: "Missar",
      detailedBreakdown: "Detaljerad breakdown",
      target: "Mål",
      shot: "Skott",
      ring: "Ring",
      holes: "hål",
      hole: "hål",
      noHole: "Inget hål",
      trainAgain: "Ny Analys",
      complete: "Analys klar!",
      error: "Kunde inte analysera bilden",
      language: "sv",
      invalidFile: "Ogiltig filtyp. Vänligen välj en bild.",
      fileReadError: "Kunde inte läsa fil",
      invalidImageData: "Ogiltig bilddata"
    },
    common: {
      back: "Tillbaka",
      saving: "Sparar..."
    },
    auth: {
      login: "Logga In",
      signup: "Registrera",
      loginDesc: "Logga in för att spara dina analyser",
      signupDesc: "Skapa ett konto för att spara dina analyser",
      email: "E-post",
      password: "Lösenord",
      firstName: "Förnamn",
      lastName: "Efternamn",
      country: "Land",
      loading: "Laddar...",
      loginError: "Inloggning misslyckades. Kontrollera dina uppgifter.",
      signupError: "Registrering misslyckades. Försök igen.",
      loginSuccess: "Inloggad!",
      signupSuccess: "Konto skapat!",
      genericError: "Ett fel uppstod. Försök igen.",
      switchToSignup: "Har du inget konto? Registrera dig",
      switchToLogin: "Har du redan ett konto? Logga in",
      forgotPassword: "Glömt Lösenord",
      forgotPasswordLink: "Glömt ditt lösenord?",
      forgotPasswordDesc: "Ange din e-post för att återställa lösenordet",
      sendResetLink: "Skicka Återställningslänk",
      resetPassword: "Återställ Lösenord",
      resetPasswordError: "Kunde inte skicka återställnings-mejl. Försök igen.",
      resetPasswordSuccess: "Återställnings-mejl skickat! Kolla din inkorg.",
      backToLogin: "Tillbaka till inloggning",
      logout: "Logga Ut"
    },
    history: {
      title: "Mina Analyser",
      loading: "Laddar...",
      noAnalyses: "Inga sparade analyser ännu",
      deleteSuccess: "Analys borttagen",
      save: "Spara Analys",
      saveSuccess: "Analys sparad!"
    },
    shotDetection: {
      title: "Skottdetektering",
      targetCount: "Antal Skott",
      sensitivity: "Detekteringskänslighet",
      thresholdValue: "Tröskel",
      shotsDetected: "Detekterade Skott",
      start: "Starta Lyssning",
      stop: "Stoppa & Återställ",
      audioLevel: "Ljudnivå",
      quiet: "Tyst",
      loud: "Högt",
      statistics: "Statistik",
      average: "Medel",
      fastest: "Snabbast",
      slowest: "Långsammast",
      shotLog: "Skottlogg",
      shot: "Skott",
      complete: "Alla skott detekterade!",
      started: "Lyssnar efter skott...",
      micError: "Kunde inte få tillgång till mikrofon",
      saveSession: "Spara Session",
      saved: "Session sparad!",
      saveError: "Kunde inte spara session",
      noShots: "Inga skott att spara",
      loginRequired: "Du måste vara inloggad för att spara sessioner"
    },
    faq: {
      title: "Vanliga Frågor",
      q1: "Vad är aimtrain?",
      a1: "aimtrain är en professionell skytteträningsapp som hjälper dig förbättra dina skyttefärdigheter genom tidsbaserade träningssekvenser, skottdetektering och AI-baserad analys.",
      q2: "Hur fungerar skottdetektering?",
      a2: "Skottdetektering använder enhetens mikrofon för att lyssna efter och registrera skott. Den mäter tiden mellan varje skott och ger detaljerad statistik om din skytteprestanda.",
      q3: "Måste jag skapa mål innan träning?",
      a3: "Nej, du kan börja träna direkt! Det tidsbaserade träningsläget låter dig ställa in antal skott och intervall mellan dem utan att skapa måltavlor.",
      q4: "Hur fungerar AI-analysen?",
      a4: "Ladda upp eller ta ett foto av ditt mål efter skytte. AI:n kommer automatiskt att detektera och analysera varje skott, beräkna din totala poäng, träffsäkerhet och ge detaljerad feedback.",
      q5: "Kan jag spara mina träningsresultat?",
      a5: "Ja! Skapa ett konto för att spara dina analyser och följa din utveckling över tid i Historik-sektionen.",
      q6: "Vilka målformer finns tillgängliga?",
      a6: "Du kan skapa anpassade måltavlor med olika former inklusive cirklar, kvadrater, trianglar, uppochnerväda trianglar, hexagoner och diamanter på olika avstånd."
    },
    about: {
      title: "Om aimtrain",
      metaDescription: "Avancerad luftgevärträningsapp med anpassningsbara måltavlor, tidsbaserade sekvenser, skottdetektering och AI-driven analys för professionell skytteträning",
      heading: "Professionell Luftgevärträning Gjord Enkel",
      intro: "aimtrain är din kompletta digitala följeslagare för luftgevärsskytte, som kombinerar traditionella skyttemetoder med modern AI-teknologi för att hjälpa dig förbättra din precision och konsistens.",
      whatIsTitle: "Vad är aimtrain?",
      whatIsContent: "aimtrain är en avancerad skytteträningsapplikation designad för luftgevärsentusiaster och professionella skyttar. Oavsett om du tränar för tävlingar eller förbättrar dina rekreationsskyttefärdigheter, ger aimtrain dig verktygen du behöver för att spåra, analysera och förbättra din prestation.",
      featuresTitle: "Huvudfunktioner",
      feature1Title: "Anpassad Målskapelse",
      feature1Content: "Designa och skriv ut anpassningsbara måltavlor med olika former (cirklar, kvadrater, trianglar, hexagoner, diamanter) på olika avstånd. Perfekt för 25m, 50m och andra standardskytteavstånd.",
      feature2Title: "Tidsbaserade Träningssekvenser",
      feature2Content: "Öva med realistiska tidsbaserade sekvenser med ljudsignaler. Konfigurera antal skott och intervall för att simulera tävlingsförhållanden eller bygga muskelminne.",
      feature3Title: "AI-driven Skottdetektering",
      feature3Content: "Använd din enhets mikrofon för att automatiskt detektera och logga varje skott. Få detaljerad statistik inklusive timing, intervaller och analys av skytterytm.",
      feature4Title: "Intelligent Resultatanalys",
      feature4Content: "Ladda upp foton av dina mål och låt vår AI analysera skottplacering, beräkna poäng och ge detaljerad feedback. Stöder flera målkonfigurationer inklusive 1, 4, 6 och 9-målstavlor.",
      whoForTitle: "Vem är aimtrain för?",
      whoForContent: "aimtrain är designad för luftgevärsskyttar på alla nivåer - från nybörjare som lär sig grunderna till tävlingsskyttar som förbereder sig för mästerskap. Den är särskilt användbar för 10m luftgevärsdiscipliner och precisionsskytteträning.",
      techTitle: "Teknologi",
      techContent: "Byggd med moderna webbteknologier fungerar aimtrain på vilken enhet som helst med en kamera och mikrofon. AI-analysen använder avancerad datorseende för att noggrant detektera skottplacering och beräkna poäng baserat på standardsystem för skyttemålspoängberäkning.",
      privacyTitle: "Integritet & Data",
      privacyContent: "Din träningsdata och analysresultat lagras säkert. Bilder bearbetas för analys och kan sparas till ditt konto för att spåra framsteg över tid. Vi delar aldrig din personliga träningsdata med tredje part."
    },
    feedback: {
      heading: "Hjälp till att förbättra AI-precisionen",
      instruction: "Din feedback hjälper till att träna AI:n att bli mer exakt. Vänligen betygsätt analysen nedan:",
      question: "Var AI-analysen korrekt?",
      thumbsUp: "Ja, korrekt",
      thumbsDown: "Nej, behöver korrigeras",
      thankYou: "Tack för din feedback!",
      correctScores: "Korrigera poängen nedan:",
      aiScore: "AI-poäng",
      range: "Område",
      submit: "Skicka Korrigeringar",
      correctionsSaved: "Korrigeringar sparade! Detta hjälper oss förbättra AI:n.",
      loginRequired: "Du måste vara inloggad för att ge feedback"
    }
  }
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>("en");

  useEffect(() => {
    const saved = localStorage.getItem("language") as Language;
    if (saved && (saved === "en" || saved === "sv")) {
      setLanguage(saved);
    }
  }, []);

  const handleSetLanguage = (lang: Language) => {
    setLanguage(lang);
    localStorage.setItem("language", lang);
  };

  const t = (key: string): string => {
    const keys = key.split(".");
    let value: any = translations[language];
    
    for (const k of keys) {
      if (value && typeof value === "object") {
        value = value[k];
      } else {
        return key;
      }
    }
    
    return typeof value === "string" ? value : key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage: handleSetLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
};